import {
  ContentDrawer,
  UserItem,
  AddLineIcon,
  CloseGreyIcon,
  ConfirmDialog,
} from '@halo/ui-chat-react';
import { useForm } from 'react-hook-form';
import {
  Stack,
  Box,
  Select,
  Flex,
  createListCollection,
  Text,
  Dialog,
  Button,
  Field,
  Input,
} from '@chakra-ui/react';
import { memo, useState } from 'react';
import { type GroupMember } from '@halo/chat-sdk';

interface ChatAdminsProps {
  trigger?: React.ReactNode;
  open?: boolean;
  member: GroupMember;
  onOpenChange?: (open: boolean) => void;
  dismissAdmin: () => void;
  onAddCustomTitle: (role: string) => void;
  onDeleteCustomTitle: () => void;
}

const SelectPanel = ({
  onAddCustomTitle,
  onDeleteCustomTitle = () => {},
}: {
  onAddCustomTitle: (role: string) => void;
  onDeleteCustomTitle?: () => void;
}) => {
  const [_value, _setValue] = useState('admin');
  const [showDialog, setShowDialog] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [removeGroupLoading, setRemoveGroupLoading] = useState(false);
  const role = createListCollection({
    items: [
      { label: 'Admin', value: 'admin' },
      { label: 'Moderator', value: 'moderator' },
      { label: 'Owner', value: 'owner' },
      { label: 'Member', value: 'member' },
    ],
  });

  const handleAddTitle = () => {
    setShowDialog(true);
  };
  return (
    <Box bg="bg" rounded="xl">
      <Select.Root
        collection={role}
        w="full"
        size="lg"
        // value={[value]}
        // onValueChange={(details) => setValue(details.value[0])}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger border="none" cursor="pointer" fontWeight="medium">
            <Select.ValueText />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Select.Positioner>
          <Select.Content p="1" rounded="xl" border="none" fontWeight="semibold">
            <Flex
              alignItems="center"
              gap="2.5"
              px="2"
              py="3"
              cursor="pointer"
              _hover={{ bg: 'grey.10', borderRadius: 'sm' }}
              onClick={handleAddTitle}
            >
              <AddLineIcon />
              <Text fontSize="sm" lineHeight="100%">
                Add Title
              </Text>
            </Flex>
            {role.items.map((item) => (
              <Select.Item
                item={item}
                key={item.value}
                px="2"
                py="3"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                _hover={{ bg: 'grey.10', borderRadius: 'sm' }}
              >
                <Box fontSize="sm" lineHeight="100%">
                  {item.label}
                </Box>
                {/* hover才出现关闭icon */}
                <Box
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setConfirm(true);
                  }}
                >
                  <CloseGreyIcon transform="scale(0.8)" />
                </Box>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
      {showDialog && (
        <AddTitleDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          onAddCustomTitle={onAddCustomTitle}
        />
      )}
      <ConfirmDialog
        disabledPortal={false}
        open={confirm}
        onClose={() => setConfirm(false)}
        title="Delete Administrator Group?"
        onOk={async () => {
          setRemoveGroupLoading(true);
          await onDeleteCustomTitle();
          setRemoveGroupLoading(false);
          setConfirm(false);
        }}
        cancelText="Reassign to Admin"
        okText="Revoke Admin"
        okButtonProps={{
          loading: removeGroupLoading,
        }}
      ></ConfirmDialog>
    </Box>
  );
};
const ChatAdmins = ({
  open,
  member,
  onOpenChange,
  dismissAdmin,
  onAddCustomTitle,
  onDeleteCustomTitle,
}: ChatAdminsProps) => {
  return (
    <ContentDrawer title="Chat Admins" open={open} onOpenChange={onOpenChange}>
      <Box bg="bg" rounded="xl" p="1" my="2">
        <UserItem
          {...member}
          hoverable={false}
          rippleEffect={false}
          cursor="pointer"
          borderColor="transparent"
        />
      </Box>
      <Stack gap="0">
        <Box py="2.5" fontSize="xs" color="fg.muted" fontWeight="medium">
          Costom Title
        </Box>
        <SelectPanel
          onAddCustomTitle={onAddCustomTitle}
          onDeleteCustomTitle={onDeleteCustomTitle}
        />
        <Text fontWeight="normal" fontSize="xs" color="fg.muted" py="1">
          A title that members will see instead of ‘Admin’
        </Text>
      </Stack>
      <Box py="3.5" bg="bg" rounded="xl" px="1" mt="2" cursor="pointer">
        <Flex
          align="center"
          justify="space-between"
          cursor="pointer"
          px="2"
          fontWeight="medium"
          onClick={dismissAdmin}
        >
          <Flex align="center" gap="2.5">
            <Text fontSize="sm" color="fg.error">
              Dismiss Admin
            </Text>
          </Flex>
        </Flex>
      </Box>
      <Box mt="8" display="flex" justifyContent="center">
        <Button rounded="xl" minW="162px" fontWeight="medium" onClick={() => {}}>
          Done
        </Button>
      </Box>
    </ContentDrawer>
  );
};

interface AddTitleDialogProps {
  open: boolean;
  onClose: () => void;
  onAddCustomTitle: (role: string) => void;
}

function AddTitleDialog({ open, onClose, onAddCustomTitle }: AddTitleDialogProps) {
  const [loading, _setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    role: string;
  }>();

  const onSubmit = (data: { role: string }) => {
    console.log('Custom title submitted:', data.role);
    onAddCustomTitle(data.role);
    onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={({ open }) => !open && onClose()} placement="center">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content rounded="xl" w="480px" bg="bg.subtle">
          <Dialog.Header pt="5" px="5" pb="2.5" justifyContent="space-between">
            <Dialog.Title fontWeight="semibold">Add Costom Title</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body px="5" pt="0">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap="0" align="flex-start">
                <Text
                  pb="2.5"
                  fontSize="xs"
                  color="fg.muted"
                  fontWeight="medium"
                  lineHeight="normal"
                >
                  Costom Title
                </Text>
                <Field.Root invalid={!!errors.role}>
                  <Input
                    background="white"
                    border="none"
                    maxLength={16}
                    {...register('role', { required: 'Costom Title' })}
                    placeholder="Costom Title"
                    _placeholder={{ color: 'fg.subtle' }}
                    autoComplete="off"
                  />
                  <Field.ErrorText>{errors?.role?.message as string}</Field.ErrorText>
                </Field.Root>
                <Text fontWeight="normal" fontSize="xs" color="fg.muted" py="1">
                  A title that members will see instead of &apos;Admin&apos;
                </Text>
              </Stack>
              <Dialog.Footer px="0" pb="0">
                <Dialog.ActionTrigger asChild>
                  <Button variant="subtle" color="fg" bg="bg.subtle" rounded="xl" minW="96px">
                    Cancel
                  </Button>
                </Dialog.ActionTrigger>
                <Button type="submit" rounded="xl" minW="96px" loading={loading}>
                  Done
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

const MemoizedChatAdmins = memo(ChatAdmins);
export { MemoizedChatAdmins as ChatAdmins };
